import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Recommendation, RecommendationPriorityTier } from '@lizrd/contracts';
import { listMembers, setRecommendationAssignee, setRecommendationPriority } from '../../lib/api';
import { PRIORITY_META } from '../../lib/graph-visuals';
import { effectiveTier } from '../CostOptimizationBoard';
import { cn } from '../../lib/utils';

const TIERS: RecommendationPriorityTier[] = ['high', 'medium', 'low'];

/**
 * Label-less priority + assignee controls for a recommendation, reused by the
 * detail view. Editable only when `canManage`; otherwise read-only. Invalidates
 * the domain's recommendations list on change, so the detail view, the Cost
 * Optimization board, and the nav badge all refresh together.
 */
export function PriorityControl({
  rec,
  envId,
  canManage,
}: {
  rec: Recommendation;
  envId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (tier: RecommendationPriorityTier | null) =>
      setRecommendationPriority(envId, rec.id, tier),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recommendations', envId] }),
  });
  const tier = effectiveTier(rec);
  const manual = rec.userPriorityTier != null;

  if (!canManage) {
    return (
      <span className="text-sm font-medium" style={{ color: PRIORITY_META[tier]?.color }}>
        {PRIORITY_META[tier]?.label ?? tier}
        {manual && ' (manual)'}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {TIERS.map((t) => {
        const meta = PRIORITY_META[t]!;
        return (
          <button
            key={t}
            type="button"
            disabled={mut.isPending}
            onClick={() => mut.mutate(t)}
            className={cn(
              'rounded-sm px-2 py-0.5 text-xs font-semibold uppercase tracking-wide transition-colors',
              tier === t ? '' : 'text-muted hover:text-haze',
            )}
            style={
              tier === t
                ? {
                    backgroundColor: `color-mix(in srgb, ${meta.color} 22%, transparent)`,
                    color: meta.color,
                  }
                : undefined
            }
          >
            {meta.label}
          </button>
        );
      })}
      {manual && (
        <button
          type="button"
          disabled={mut.isPending}
          onClick={() => mut.mutate(null)}
          className="rounded-sm px-2 py-0.5 text-xs font-semibold text-muted hover:text-haze"
          title="Clear the manual override — revert to platform priority"
        >
          Auto
        </button>
      )}
    </div>
  );
}

export function AssigneeControl({
  rec,
  envId,
  canManage,
}: {
  rec: Recommendation;
  envId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['members'], queryFn: listMembers, enabled: canManage });
  const mut = useMutation({
    mutationFn: (assignedToId: string | null) =>
      setRecommendationAssignee(envId, rec.id, assignedToId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recommendations', envId] }),
  });
  const eligible = (members.data ?? []).filter(
    (m) => m.role === 'ADMINISTRATOR' || m.role === 'MANAGER' || m.domainIds.includes(envId),
  );

  if (!canManage) {
    return <span className="text-sm text-bone">{rec.assignedToName ?? 'Unassigned'}</span>;
  }
  return (
    <select
      disabled={mut.isPending}
      value={rec.assignedToId ?? ''}
      onChange={(e) => mut.mutate(e.target.value || null)}
      className="rounded-sm border border-hairline bg-surface-2 px-2 py-1 text-sm text-haze"
    >
      <option value="">Unassigned</option>
      {eligible.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name ?? m.email}
        </option>
      ))}
    </select>
  );
}
